import { colorStopToString, fixTransparentColorStops } from '../color-utils.js';

/**
 * Render a repeating conic gradient
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {Object} gradientDef - Gradient definition from parser
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @returns {CanvasGradient} Canvas gradient object
 */
export function renderRepeatingConicGradient(context, gradientDef, { x, y, w, h }) {
  // Parse position from orientation
  const position = gradientDef.orientation?.position;
  let centerX = w * 0.5;
  let centerY = h * 0.5;

  if (position) {
    if (position.x?.type === '%') {
      centerX = w * (parseFloat(position.x.value) / 100);
    }
    if (position.y?.type === '%') {
      centerY = h * (parseFloat(position.y.value) / 100);
    }
  }

  // Parse starting angle
  let startAngle = 0;
  if (gradientDef.orientation?.value) {
    startAngle = parseFloat(gradientDef.orientation.value);
    startAngle = ((startAngle % 360) + 360) % 360;
    startAngle = (startAngle * Math.PI) / 180 - Math.PI / 2;
  }

  const gradient = context.createConicGradient(startAngle, x + centerX, y + centerY);

  // Clone colorStops to avoid mutating the original
  const colorStops = gradientDef.colorStops.map((stop) => ({ ...stop }));

  // Fix transparent color stops to match CSS premultiplied alpha behavior
  fixTransparentColorStops(colorStops);

  // Calculate pattern length from color stops (in degrees, normalized to 0-1)
  let patternEnd = 0;
  colorStops.forEach((stop) => {
    if (stop.length?.type === '%') {
      patternEnd = Math.max(patternEnd, parseFloat(stop.length.value) / 100);
    } else if (stop.length?.type === 'deg') {
      patternEnd = Math.max(patternEnd, parseFloat(stop.length.value) / 360);
    }
  });

  // If no explicit pattern length, use the full circle
  if (patternEnd === 0) {
    patternEnd = 1;
  }

  // Calculate number of repetitions
  const repetitions = Math.ceil(1 / patternEnd);

  // Add repeated color stops
  for (let i = 0; i < repetitions; i++) {
    colorStops.forEach((stop, index) => {
      let stopPosition = i * patternEnd;

      if (stop.length?.type === '%') {
        stopPosition += (parseFloat(stop.length.value) / 100) * patternEnd;
      } else if (stop.length?.type === 'deg') {
        stopPosition += (parseFloat(stop.length.value) / 360) * patternEnd;
      } else {
        // Default distribution within pattern
        stopPosition += (index / (colorStops.length - 1)) * patternEnd;
      }

      const colorValue = colorStopToString(stop);

      if (colorValue && stopPosition <= 1) {
        gradient.addColorStop(Math.max(0, Math.min(1, stopPosition)), colorValue);
      }
    });
  }

  return gradient;
}
